import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "./i18n";
import FlowLoader from "./components/FlowLoader";
import {
    isRouteAllowed,
    readUserRole,
    useModuleVisibility,
} from "./components/helpers/homeModuleVisibility";
import { isManagerPlus, meetsFloor, readUserPosition } from "./access/positions";
import { useAccess } from "./access/AccessContext";
import { ReadOnlyProvider, ReadOnlyBanner } from "./access/ReadOnly";
import { readAccountSetup, readRequirePasswordChange } from "./components/helpers/authFlags";

interface ProtectedRouteProps {
    children: React.ReactNode,
    /** Rank floor. Used by /admin pages, which aren't modules and so have
     *  no toggle in Settings › App Modules. */
    level?: string,
    /** Module key from HOME_MODULES (e.g. "put_away", "dispatch.flagged").
     *  When set, access is decided by AccessContext (GET /access-control/my)
     *  — while the system is in "legacy" mode the BE computes this from the
     *  same per-warehouse module-visibility map the Home tile reads, so the
     *  Settings toggles remain the source of truth today. Falls back to the
     *  pre-access-control fetch of that map directly if the access-context
     *  fetch itself failed, exactly reproducing the previous behaviour. */
    module?: string,
    /** Access-control key from ACCESS_CONTROL_PLAN.md §4 (e.g.
     *  "admin.dashboard", "admin.settings.access_control"). Used by /admin
     *  routes in place of `level="admin"`. Falls back to the Manager+ rank
     *  floor if the access-context fetch failed. */
    access?: string,
    /** Hub pages (Admin landing) are gated by an AREA menu key whose read and
     *  write mean the same thing — no read-only banner or disabling there;
     *  each sub-page decides its own read/write from its own key. */
    hub?: boolean,
}

const readWarehouse = (): string | undefined => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return undefined;
        const parsed = JSON.parse(raw);
        return typeof parsed?.warehouse === "string" ? parsed.warehouse : undefined;
    } catch {
        return undefined;
    }
};

// Bounces a signed-in user who may not open this route. Sends them Home —
// never to /login, which would look like (and historically be mistaken for)
// a session expiry, even though the session is untouched.
const NoAccessRedirect = () => {
    const { t } = useTranslation();
    // Fire in an effect, not during render: a toast is a side effect, and
    // React may render this twice (StrictMode) before committing.
    useEffect(() => {
        toast.error(t("no_module_access"), { toastId: "no-module-access" });
    }, [t]);
    return <Navigate to="/" replace />;
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, level, module, access, hub = false }) => {
    const location = useLocation();
    const storedLoginFlag = window.localStorage.getItem("isLoggedIn");
    const isLoggedIn: boolean = storedLoginFlag !== null && storedLoginFlag !== "false";

    // Only module routes need the settings map; /admin routes skip the fetch.
    // readWarehouse() can briefly return undefined on the first render of an
    // SPA navigation (before the user object settles). Passing that straight
    // to useModuleVisibility used to yield {map:null, loading:false} — and the
    // guard then denied an admin on that empty-warehouse frame, before the
    // real warehouse ("Pembroke") loaded. So: while a module route is active
    // but the warehouse isn't resolved yet, treat it as still loading.
    const warehouse = readWarehouse();
    const warehouseUnresolved = !!module && !warehouse;
    // Always called (hook rules) — cheap and non-fetching outside an
    // <AccessProvider> (the permissive stub), so this is safe on every
    // route regardless of whether it declares `access` or `module`.
    const accessCtx = useAccess();
    // The per-warehouse settings map is the FALLBACK for module routes: it is
    // only fetched while the access context is not authoritative (still
    // loading, or failed). Once the context has answered, the guard decides
    // from it alone and no /settings round trip is made.
    // A refresh failure with a map loaded earlier this session keeps deciding from that map
    // (review 2026-09-24 AC-07); only a cold failure drops to the rank / App Modules fallbacks.
    const decides = accessCtx.ready && (!accessCtx.failed || accessCtx.stale);
    const contextAuthoritative = decides;
    const { map, loading, failed, resolved } = useModuleVisibility(module && !contextAuthoritative ? warehouse : undefined);

    // Record where the user was headed so login can return them there.
    // `replace` keeps /login from stacking up in history on repeated bounces.
    if (!isLoggedIn) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Forced one-time account setup / password rotation survive a refresh:
    // the login response stored the flag (helpers/authFlags), the screen
    // that satisfies it clears it. Both targets sit OUTSIDE ProtectedRoute,
    // so this cannot loop. Setup first — it includes the password step.
    if (readAccountSetup()) {
        return <Navigate to="/account-setup" replace />;
    }
    if (readRequirePasswordChange()) {
        return <Navigate to="/change-password" replace />;
    }

    const role = readUserRole();

    // A module route whose warehouse hasn't resolved yet must wait, not decide.
    if (warehouseUnresolved) {
        return <FlowLoader className="protected-route-loader" />;
    }

    // Rank floor, when one is declared. Independent of the module/access
    // check — a route may declare either or both. `level` is one of the
    // legacy rank names ("basic"/"admin"/"superAdmin"); meetsFloor() treats
    // them as aliases for the position floors (any / Manager+ / Admin) so a
    // route declared before the position migration keeps meaning what it
    // meant before, for both legacy and position-carrying sessions.
    let denied = false;
    if (level) {
        if (!meetsFloor(readUserPosition(), level as Parameters<typeof meetsFloor>[1])) {
            denied = true;
        }
    }

    // Access-control key (§4 keys, mostly /admin routes). Decided by
    // AccessContext once its fetch has settled; falls back to the Manager+
    // rank floor — the pre-access-control behaviour of `level="admin"` —
    // if the fetch itself failed, so a flaky /access-control/my call can
    // never lock out a legitimate admin.
    if (!denied && access) {
        if (!accessCtx.ready) {
            return <FlowLoader className="protected-route-loader" />;
        }
        if (decides) {
            if (!accessCtx.hasAccess(access)) denied = true;
        } else if (!isManagerPlus(readUserPosition())) {
            denied = true;
        }
    }

    if (!denied && module) {
        if (!accessCtx.ready) {
            return <FlowLoader className="protected-route-loader" />;
        }
        if (decides) {
            if (!accessCtx.hasAccess(module)) denied = true;
        } else {
            // EXISTING (pre-access-control) fallback: read the per-warehouse
            // module-visibility map directly. Never decide on a half-loaded
            // map: a hard refresh or SPA nav onto /put-away must not bounce
            // the user before their settings arrive. `resolved` is false
            // until the fetch has actually settled (map, empty, or failed) —
            // the synchronous seed can briefly report loading:false with a
            // null map on a re-render, which must NOT count as "decide now".
            if (loading || !resolved) {
                return <FlowLoader className="protected-route-loader" />;
            }
            // The settings fetch ultimately failed (after retries). We have NO
            // real map, so we cannot honour the toggles — but denying here would
            // lock a legitimate admin out of a superAdmin-default module purely
            // because their /settings call flaked. Fail OPEN to the user's rank:
            // basic keeps only always-visible-default modules, admin/superAdmin
            // get through. The BE still enforces real permission on every
            // endpoint the page then calls, so this grants no data access.
            if (failed) {
                if (!isManagerPlus(readUserPosition()) && !isRouteAllowed(null, module, role)) {
                    denied = true;
                }
            } else if (!isRouteAllowed(map, module, role)) {
                denied = true;
            }
        }
    }

    if (denied) {
        return <NoAccessRedirect />;
    }

    // Read-only handheld mode (ACCESS_CONTROL_PLAN.md §8.4): when the
    // access context is authoritative (ready, not failed) and the resolved
    // level for this route's key is "read" rather than "write", wrap the
    // page so useReadOnly() disables its mutating controls and scans, with
    // a persistent banner above it. Only decided once the context is
    // authoritative — the fallback paths above have no read/write notion.
    const effectiveKey = access ?? module;
    if (effectiveKey && !hub && decides && accessCtx.level(effectiveKey) === "read") {
        return (
            <ReadOnlyProvider value={true}>
                <ReadOnlyBanner />
                {children}
            </ReadOnlyProvider>
        );
    }

    return <>{children}</>
};

export default ProtectedRoute;
